import { useMutation, useQuery } from '@tanstack/react-query';
import {
  createEvenementInscription,
  createInscription,
  createReservation,
  type ReservationCreate,
  getEvenement,
  getFormateur,
  getFormationBySlug,
  getSiteContent,
  listFeaturedGallery,
  listFormateurs,
  listGalleryByFormation,
  listGalleryForMember,
  listForumPostsForMember,
  listPublicForumPosts,
  listInscriptionsByMember,
  listEvenementInscriptionsByMember,
  listPublishedEvenements,
  listPublishedFormations,
  listPublishedGallery,
  listPublishedPartenaires,
  listPublishedTestimonials,
  getPartenaire,
  updateEvenementInscription,
  type Evenement,
  type EvenementInscription,
  type EvenementInscriptionCreate,
  type ForumPost,
  type GalleryItem,
  type Inscription,
  type InscriptionCreate,
  type Partenaire,
  type SiteContent,
} from '@mtc/shared';
import { adaptFormateur, adaptFormation } from './adapters';
import type { Formateur, Formation, Temoignage } from '../types';

// ──────────────────────────────────────────────────────────────────────────
// Query keys — keep them centralised so invalidation is obvious.
// ──────────────────────────────────────────────────────────────────────────
export const queryKeys = {
  formations: ['formations'] as const,
  formation: (id: string) => ['formation', id] as const,
  formateurs: ['formateurs'] as const,
  formateur: (id: string) => ['formateur', id] as const,
  testimonials: ['testimonials'] as const,
  siteContent: ['siteContent'] as const,
  evenements: ['evenements'] as const,
  evenement: (id: string) => ['evenement', id] as const,
  gallery: ['gallery'] as const,
  galleryFeatured: ['gallery', 'featured'] as const,
  galleryByFormation: (formationId: string) => ['gallery', 'formation', formationId] as const,
  galleryForMember: (uid: string) => ['gallery', 'member', uid] as const,
  myInscriptions: (uid: string) => ['my-inscriptions', uid] as const,
  partenaires: ['partenaires'] as const,
  partenaire: (id: string) => ['partenaire', id] as const,
  forumFeed: (uid: string) => ['forum-feed', uid] as const,
  forumPublic: ['forum-public'] as const,
};

/**
 * Member publications feed — public + all-member + their formations,
 * pinned-first. Empty when not enrolled (use usePublicForumFeed then).
 */
export function useForumFeed(uid: string | undefined, enrolledFormationIds: string[] | undefined) {
  return useQuery<ForumPost[]>({
    queryKey: queryKeys.forumFeed(uid ?? ''),
    queryFn: () => listForumPostsForMember(enrolledFormationIds ?? []),
    enabled: !!uid,
  });
}

/** 'Grand public' publications — visible to everyone, incl. anonymous visitors. */
export function usePublicForumFeed(enabled = true) {
  return useQuery<ForumPost[]>({
    queryKey: queryKeys.forumPublic,
    queryFn: () => listPublicForumPosts(),
    enabled,
  });
}

export function usePartenaires() {
  return useQuery<Partenaire[]>({
    queryKey: queryKeys.partenaires,
    queryFn: () => listPublishedPartenaires(),
    staleTime: 5 * 60_000,
  });
}

export function usePartenaire(id: string | undefined) {
  return useQuery<Partenaire | null>({
    queryKey: queryKeys.partenaire(id ?? ''),
    queryFn: async () => (id ? getPartenaire(id) : null),
    enabled: !!id,
  });
}

// ──────────────────────────────────────────────────────────────────────────
// Formations
// ──────────────────────────────────────────────────────────────────────────
export function useFormations() {
  return useQuery<Formation[]>({
    queryKey: queryKeys.formations,
    queryFn: async () => {
      // Return ALL published formations (including those whose éditions have
      // passed). Discovery surfaces (catalogue, home, formation detail) show
      // them all; date-driven surfaces filter themselves — the Calendrier only
      // renders `upcomingSessions`, and the inscription form only offers
      // formations with an upcoming édition.
      const docs = await listPublishedFormations();
      return docs.map(adaptFormation);
    },
  });
}

export function useFormation(id: string | undefined) {
  return useQuery<Formation | null>({
    queryKey: queryKeys.formation(id ?? ''),
    queryFn: async () => {
      if (!id) return null;
      const doc = await getFormationBySlug(id);
      return doc ? adaptFormation(doc) : null;
    },
    enabled: !!id,
  });
}

// ──────────────────────────────────────────────────────────────────────────
// Formateurs
// ──────────────────────────────────────────────────────────────────────────
export function useFormateurs() {
  return useQuery<Formateur[]>({
    queryKey: queryKeys.formateurs,
    queryFn: async () => {
      const docs = await listFormateurs();
      return docs.map(adaptFormateur);
    },
  });
}

export function useFormateursById() {
  const q = useFormateurs();
  return {
    ...q,
    /** Lookup table keyed by formateur slug. */
    byId: (q.data ?? []).reduce<Record<string, Formateur>>((acc, fo) => {
      acc[fo.id] = fo;
      return acc;
    }, {}),
  };
}

export function useFormateur(id: string | undefined) {
  return useQuery<Formateur | null>({
    queryKey: queryKeys.formateur(id ?? ''),
    queryFn: async () => {
      if (!id) return null;
      const doc = await getFormateur(id);
      return doc ? adaptFormateur(doc) : null;
    },
    enabled: !!id,
  });
}

// ──────────────────────────────────────────────────────────────────────────
// Inscriptions
// ──────────────────────────────────────────────────────────────────────────
export function useSubmitInscription() {
  return useMutation({
    mutationFn: (input: InscriptionCreate & { memberUid?: string | null }) =>
      createInscription(input),
  });
}

export function useSubmitReservation() {
  return useMutation({
    mutationFn: (input: ReservationCreate) => createReservation(input),
  });
}

export function useMyInscriptions(uid: string | undefined) {
  return useQuery<Inscription[]>({
    queryKey: queryKeys.myInscriptions(uid ?? ''),
    queryFn: () => (uid ? listInscriptionsByMember(uid) : Promise.resolve([])),
    enabled: !!uid,
  });
}

// ──────────────────────────────────────────────────────────────────────────
// Evenement inscriptions (hosted-mtc registrations)
// ──────────────────────────────────────────────────────────────────────────
export function useSubmitEvenementInscription() {
  return useMutation({
    mutationFn: (input: EvenementInscriptionCreate & { memberUid?: string | null }) =>
      createEvenementInscription(input),
  });
}

export function useMyEvenementInscriptions(uid: string | undefined) {
  return useQuery<EvenementInscription[]>({
    queryKey: ['my-evenement-inscriptions', uid ?? ''],
    queryFn: () =>
      uid ? listEvenementInscriptionsByMember(uid) : Promise.resolve([]),
    enabled: !!uid,
  });
}

export function useUpdateMyEvenementInscription() {
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      updateEvenementInscription(id, patch),
  });
}

// ──────────────────────────────────────────────────────────────────────────
// Evenements (scientific events curated for the community)
// ──────────────────────────────────────────────────────────────────────────
export function useEvenements() {
  return useQuery<Evenement[]>({
    queryKey: queryKeys.evenements,
    queryFn: () => listPublishedEvenements(),
  });
}

export function useEvenement(id: string | undefined) {
  return useQuery<Evenement | null>({
    queryKey: queryKeys.evenement(id ?? ''),
    queryFn: () => (id ? getEvenement(id) : Promise.resolve(null)),
    enabled: !!id,
  });
}

// ──────────────────────────────────────────────────────────────────────────
// Gallery
// ──────────────────────────────────────────────────────────────────────────
export function useGallery() {
  return useQuery<GalleryItem[]>({
    queryKey: queryKeys.gallery,
    queryFn: () => listPublishedGallery(),
  });
}

/** Photos flagged `featuredOnHome: true` — used by the home carousel only. */
export function useFeaturedGallery() {
  return useQuery<GalleryItem[]>({
    queryKey: queryKeys.galleryFeatured,
    queryFn: () => listFeaturedGallery(),
  });
}

/** Photos tagged to a specific formation across all sessions. */
export function useGalleryByFormation(formationId: string | undefined) {
  return useQuery<GalleryItem[]>({
    queryKey: queryKeys.galleryByFormation(formationId ?? ''),
    queryFn: () => (formationId ? listGalleryByFormation(formationId) : Promise.resolve([])),
    enabled: !!formationId,
  });
}

/**
 * Member-side gallery: every photo with `visibility in ['public', 'members']`.
 * The page filters per inscription (formationId + sessionId) client-side.
 * Requires the user to be signed in — the `enabled` gate makes that explicit.
 */
export function useGalleryForMember(uid: string | undefined) {
  return useQuery<GalleryItem[]>({
    queryKey: queryKeys.galleryForMember(uid ?? ''),
    queryFn: () => (uid ? listGalleryForMember() : Promise.resolve([])),
    enabled: !!uid,
  });
}

// ──────────────────────────────────────────────────────────────────────────
// Site content (singleton — admin-editable copy for hero / about / footer / stats)
// ──────────────────────────────────────────────────────────────────────────
export function useSiteContent() {
  return useQuery<SiteContent | null>({
    queryKey: queryKeys.siteContent,
    queryFn: () => getSiteContent(),
    // Site content is admin-edited rarely but read on every page — long cache.
    staleTime: 5 * 60_000,
  });
}

// ──────────────────────────────────────────────────────────────────────────
// Testimonials
// ──────────────────────────────────────────────────────────────────────────
export function useTemoignages() {
  return useQuery<Temoignage[]>({
    queryKey: queryKeys.testimonials,
    queryFn: async () => {
      const docs = await listPublishedTestimonials();
      return docs.map((t) => ({
        auteur: t.author,
        role: t.role,
        texte: t.quote,
        formation: t.formationId ?? '',
        note: 5,
      }));
    },
  });
}
